<script lang="ts">
  import { onMount, untrack } from "svelte"
  import ContactOrderPlanningSurface, {
    type ContactOrderPlanningDraft,
  } from "../../../../../../../../../../_shared/_modules/contact-order-planning-surface"
  import { domainContactOrderPlanningOperations } from "../../../../../../../../operations"
  import type {
    ContactOrderPlanningContinuationResult,
    ContactOrderPlanningSubmissionResult,
  } from "../../../../../../../../../../_shared/_logic/contact-orders-contract"
  import { loadBatchContactPoints } from "./_actions/contactPointLoad"
  import type {
    BatchUploadInit,
    BatchUploadReferenceData,
    BatchUploadStepContext,
    BatchUploadWorkflowEvent,
    ContactOrdersOutput,
    CreateWorkOrdersOutput,
    PlanAppointmentsOutput,
  } from "../../_shared/_logic/batch-upload-contract"

  type Props = {
    ctx: BatchUploadStepContext
    planningDraft: ContactOrderPlanningDraft
    init: BatchUploadInit
    referenceData: BatchUploadReferenceData
    loadList: CreateWorkOrdersOutput
    planAppointments: PlanAppointmentsOutput
    output?: ContactOrdersOutput
    onEvent: (event: BatchUploadWorkflowEvent) => void
  }

  const {
    ctx,
    planningDraft,
    init,
    referenceData,
    loadList,
    planAppointments,
    output = undefined,
    onEvent,
  }: Props = $props()

  const events = $derived(
    output?.scheduleEventsAfterContactPlanning ?? planAppointments.createdEvents,
  )
  const contactOrders = $derived(output?.createdContactOrders ?? [])
  const employeeOptions = $derived(
    referenceData.employeeOptions
      .map((option) => ({ label: option.label, value: String(option.value ?? "") }))
      .filter((option) => option.value),
  )
  const caseIdByOrderId = $derived(
    Object.fromEntries(
      loadList.createdRows
        .map((row) => row.workOrder)
        .map((order) => [order.id, order.case_id ?? null]),
    ),
  )
  let loadedContactPoints = $state<ContactPoint[]>([])
  let contactPointsLoading = $state(true)
  let contactPointsLoadError = $state<string | null>(null)
  let effectiveContactPoints = $state<ContactPoint[]>(untrack(() => init.contactPoints))

  const loadRelatedContactPoints = async () => {
    if (output) {
      contactPointsLoading = false

      return
    }

    if (!init.organizationId || !loadList.createdRows.map((row) => row.workOrder).length) {
      contactPointsLoading = false

      return
    }

    contactPointsLoading = true
    contactPointsLoadError = null
    try {
      const result = await loadBatchContactPoints({
        organizationId: init.organizationId,
        seedContactPoints: init.contactPoints,
        workOrders: loadList.createdRows.map((row) => row.workOrder),
      })
      loadedContactPoints = result.loadedContactPoints
      effectiveContactPoints = result.effectiveContactPoints
    } catch (err) {
      console.error("batch_contact_order_contact_points_load_failed", err)
      contactPointsLoadError = "Kontaktwege konnten nicht vollständig geladen werden."
      ctx.setError?.(
        "Kontaktwege konnten nicht vollständig geladen werden. Bitte versuchen Sie es erneut.",
      )
    } finally {
      contactPointsLoading = false
    }
  }

  const buildOutput = (result: ContactOrderPlanningContinuationResult): ContactOrdersOutput => ({
    createdContactOrders: result.createdContactOrders,
    scheduleEventsAfterContactPlanning: result.scheduleEvents,
    dirtyEventsPersisted: result.dirtyEventsPersisted,
    selectedTemplateIds: [],
  })

  const completeStep = async (result: ContactOrderPlanningContinuationResult) => {
    onEvent({ type: "contact_orders_completed", output: buildOutput(result) })
    await ctx.requestNext?.()
  }

  const handleSubmissionResult = async (result: ContactOrderPlanningSubmissionResult) => {
    if (result.kind === "removed") return

    if (result.kind === "committed") {
      if (result.completedCount) await init.onWorkflowMutated(result.workOrders)

      if (result.status !== "completed") return
    }

    await completeStep(result.result)
  }

  onMount(() => {
    void loadRelatedContactPoints()
  })
</script>

{#if contactPointsLoadError}
  <p class="projection-state projection-state--warning">{contactPointsLoadError}</p>
{/if}
{#key contactPointsLoading ? "contact-points-loading" : "contact-points-ready"}
  <ContactOrderPlanningSurface
    {planningDraft}
    organizationId={init.organizationId}
    {events}
    {contactOrders}
    workOrders={loadList.createdRows.map((row) => row.workOrder)}
    properties={loadList.propertyPool}
    customers={init.customers}
    contactPoints={effectiveContactPoints}
    {employeeOptions}
    {caseIdByOrderId}
    operations={domainContactOrderPlanningOperations}
    locked={Boolean(output)}
    readOnly={ctx.readOnly}
    loading={contactPointsLoading}
    helpModalTextId="workOrderBatchContactOrders"
    setError={ctx.setError}
    onSubmissionResult={handleSubmissionResult}
  />
{/key}

<style lang="less">
  .projection-state {
    margin: 0;
    color: color-mix(in srgb, var(--text-invers-100) 78%, transparent);
    font-size: 0.9rem;
    line-height: 1.35;
  }

  .projection-state--warning {
    color: color-mix(in srgb, var(--warning-100, #ffbf47) 86%, var(--text-invers-100));
  }
</style>
